import { useState } from "react";
import { X, Lock, Mail, User as UserIcon, Loader2, Scissors } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/src/lib/useAuth";

export function AuthModal() {
  const { isAuthModalOpen, closeAuthModal, authModalMode, signInWithEmail, registerWithEmail } = useAuth();
  const [mode, setMode] = useState<"login" | "register">(authModalMode);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (!isAuthModalOpen) return null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      if (mode === "login") {
        await signInWithEmail(email, password);
      } else {
        if (!name.trim()) throw new Error("Please enter your full name.");
        await registerWithEmail(name, email, password);
      }
      closeAuthModal();
      setName("");
      setEmail("");
      setPassword("");
    } catch (err: any) {
      setError(err.message || "An error occurred during authentication.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-md">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 10 }}
          className="relative w-full max-w-md overflow-hidden rounded-3xl bg-neutral-900 border border-white/10 p-8 shadow-2xl text-white"
        >
          {/* Close button */}
          <button
            onClick={closeAuthModal}
            aria-label="Close authentication dialog"
            className="absolute top-5 right-5 p-2 text-neutral-400 hover:text-white hover:bg-white/10 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Header */}
          <div className="flex flex-col items-center text-center mb-6">
            <div className="w-12 h-12 rounded-2xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-400 mb-3">
              <Scissors className="w-6 h-6" />
            </div>
            <h2 className="text-2xl font-black tracking-tight">
              {mode === "login" ? "Welcome Back to ParberKon" : "Create Your Account"}
            </h2>
            <p className="text-sm text-neutral-400 mt-1">
              {mode === "login"
                ? "Sign in to save favorite shops & post verified reviews"
                : "Join the premier Cyprus barbershop directory community"}
            </p>
          </div>

          {/* Switch tabs */}
          <div className="flex rounded-full bg-white/5 p-1 mb-6 border border-white/10">
            <button
              type="button"
              onClick={() => { setMode("login"); setError(""); }}
              className={`flex-1 py-2 text-xs font-semibold rounded-full transition-all ${
                mode === "login" ? "bg-emerald-500 text-black shadow" : "text-neutral-400 hover:text-white"
              }`}
            >
              Sign In
            </button>
            <button
              type="button"
              onClick={() => { setMode("register"); setError(""); }}
              className={`flex-1 py-2 text-xs font-semibold rounded-full transition-all ${
                mode === "register" ? "bg-emerald-500 text-black shadow" : "text-neutral-400 hover:text-white"
              }`}
            >
              Create Account
            </button>
          </div>

          {/* Error notice */}
          {error && (
            <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-xs font-medium">
              {error}
            </div>
          )}

          {/* Form */}
          <form
            onSubmit={handleSubmit}
            name="user-authentication-form"
            toolname="authenticate_user"
            tool-name="authenticate_user"
            tooldescription="User sign in and registration form for ParberKon"
            tool-description="User sign in and registration form for ParberKon"
            data-webmcp-tool="user_authentication"
            data-webmcp-description="User sign in and registration form for ParberKon"
            itemScope
            itemType="https://schema.org/Form"
            className="space-y-4"
          >
            {mode === "register" && (
              <div className="relative">
                <UserIcon className="absolute left-3.5 top-3.5 w-4 h-4 text-neutral-400" />
                <Input
                  type="text"
                  name="name"
                  placeholder="Full Name"
                  toolparamdescription="User display name for registration"
                  tool-param-description="User display name for registration"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-neutral-500 rounded-xl"
                  required
                />
              </div>
            )}

            <div className="relative">
              <Mail className="absolute left-3.5 top-3.5 w-4 h-4 text-neutral-400" />
              <Input
                type="email"
                name="email"
                placeholder="Email Address"
                toolparamdescription="User email address for authentication"
                tool-param-description="User email address for authentication"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-neutral-500 rounded-xl"
                required
              />
            </div>

            <div className="relative">
              <Lock className="absolute left-3.5 top-3.5 w-4 h-4 text-neutral-400" />
              <Input
                type="password"
                name="password"
                placeholder="Password (min 8 characters)"
                toolparamdescription="Account password"
                tool-param-description="Account password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-neutral-500 rounded-xl"
                minLength={8}
                required
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-emerald-500 hover:bg-emerald-400 text-black font-bold h-11 rounded-xl transition-colors mt-2"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : mode === "login" ? (
                "Sign In"
              ) : (
                "Register Account"
              )}
            </Button>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
